/**
 * FCR 19.1 Suspected Misuse — monthly per-SKU order-limit normalization contract.
 *
 * `POST api/cart/normalize` returns the per-product outcome of validating the cart against the
 * monthly purchase limit (auto-reduce / auto-remove). Keep everything the app consumes derived from
 * the internal model + mapper below, so a wire-shape change only touches this file + OrderLimitService.
 *
 * Wire shape (camelCase, per BE):
 *   { lines: [ { productId, updatedQuantity, alreadyPurchased, maxAllowed, status } ] }
 */

export enum OrderLimitStatus {
  /** Within the monthly limit — no change. */
  Valid = 'Valid',
  /** Quantity auto-reduced to the remaining monthly limit. */
  Reduced = 'Reduced',
  /** Product auto-removed to stay within the monthly limit. */
  Removed = 'Removed',
}

/** Wire DTO — matches the backend `cart/normalize` line shape (camelCase). */
export interface OrderLimitAdjustmentItemDto {
  productId: number;
  /** New quantity after adjustment. */
  updatedQuantity: number;
  /** Quantity already purchased this calendar month (historical lookup). */
  alreadyPurchased: number;
  /** Configured monthly limit for this SKU (e.g. 50). */
  maxAllowed: number;
  status: OrderLimitStatus | string;
}

export interface OrderLimitAdjustmentDto {
  lines: OrderLimitAdjustmentItemDto[];
}

/** Internal model — what the app consumes. */
export interface OrderLimitAdjustmentItem {
  productId: number;
  updatedQuantity: number;
  alreadyPurchased: number;
  maxAllowed: number;
  status: OrderLimitStatus;
}

export interface OrderLimitAdjustment {
  products: OrderLimitAdjustmentItem[];
}

const toStatus = (status: OrderLimitStatus | string): OrderLimitStatus =>
  Object.values(OrderLimitStatus).includes(status as OrderLimitStatus)
    ? (status as OrderLimitStatus)
    : OrderLimitStatus.Valid;

export const mapOrderLimitAdjustmentToInternal = (dto: OrderLimitAdjustmentDto): OrderLimitAdjustment => ({
  products: (dto?.lines ?? []).map((item) => ({
    productId: item.productId,
    updatedQuantity: item.updatedQuantity,
    alreadyPurchased: item.alreadyPurchased,
    maxAllowed: item.maxAllowed,
    status: toStatus(item.status),
  })),
});
